import { useState, useEffect, useRef } from 'react';
import { 
  queryCollection, 
  getOrganizationBySlug,
  getPortalData,
  Organization,
  Announcement,
  Document as DocType
} from '../../lib/api';
import { 
  PortalSettings, 
  PortalData, 
  TemplateId,
  TEMPLATE_CONFIGS 
} from '../../types/portal';
import {
  getCachedData,
  setCachedData,
  isCacheFresh,
  PORTAL_CACHE_KEYS
} from '../../lib/portalCache';
import ModernTemplate from './ModernTemplate';
import ClassicTemplate from './ClassicTemplate';
import MinimalistTemplate from './MinimalistTemplate';
import { Buildings } from '@phosphor-icons/react';

interface PortalRendererProps {
  slug: string;
  isAuthenticated?: boolean;
}

// Template component map
const TEMPLATE_COMPONENTS: Record<TemplateId, React.FC<{ data: PortalData }>> = {
  modern: ModernTemplate,
  classic: ClassicTemplate,
  minimalist: MinimalistTemplate
};

export default function PortalRenderer({ slug, isAuthenticated = false }: PortalRendererProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const isRevalidating = useRef(false);

  useEffect(() => {
    loadPortalData();
  }, [slug, isAuthenticated]);

  // Update document title when portal data is loaded
  useEffect(() => {
    if (portalData?.settings?.meta_title) {
      document.title = portalData.settings.meta_title;
    } else if (portalData?.organization?.name) {
      document.title = portalData.organization.name;
    }
    
    // Cleanup: Reset title when unmounting
    return () => {
      document.title = 'Förvaltningen';
    };
  }, [portalData]);

  const loadPortalData = async () => {
    if (!slug) {
      setError('Ingen organisation angiven');
      setLoading(false);
      return;
    }

    // Check cache first (stale-while-revalidate pattern)
    const cachedData = getCachedData<PortalData>(PORTAL_CACHE_KEYS.FULL_DATA, slug);
    
    if (cachedData) {
      // Show cached data immediately
      setPortalData(cachedData);
      setLoading(false);
      
      // If cache is still fresh, don't fetch again
      if (isCacheFresh(PORTAL_CACHE_KEYS.FULL_DATA, slug)) {
        console.log('PortalRenderer: Using fresh cached data');
        return;
      }
      
      // Cache is stale - revalidate in background
      console.log('PortalRenderer: Showing cached data, revalidating in background...');
      if (!isRevalidating.current) {
        isRevalidating.current = true;
        fetchFreshData(true).finally(() => {
          isRevalidating.current = false;
        });
      }
      return;
    }

    // No cache - fetch fresh data
    setLoading(true);
    setError(null);
    await fetchFreshData(false);
  };

  const fetchFreshData = async (isBackgroundFetch: boolean) => {
    console.log('PortalRenderer: Fetching fresh data for slug:', slug, isBackgroundFetch ? '(background)' : '');

    try {
      // 1. Get organization by slug
      const org = await getOrganizationBySlug(slug);
      
      if (!org) {
        if (!isBackgroundFetch) {
          setError('Föreningen hittades inte');
          setLoading(false);
        }
        return;
      }

      // 2. Try to get custom portal_settings from CommHub
      let portalSettings: PortalSettings | null = null;
      try {
        const settings = await queryCollection<PortalSettings>('portal_settings', {
          organization_id: org.id
        });
        if (settings.length > 0) {
          portalSettings = settings[0];
        }
      } catch (e) {
        console.log('No custom portal settings found, using defaults', e);
      }

      // 3. If no custom settings, create default from organization's portal_settings
      if (!portalSettings) {
        const defaultTemplate = TEMPLATE_CONFIGS[0]; // Modern
        portalSettings = {
          organization_id: org.id,
          template_id: 'modern',
          colors: org.portal_settings?.background_color 
            ? {
                primary: '#3B82F6',
                secondary: '#1E40AF',
                accent: '#F59E0B',
                background: org.portal_settings.background_color,
                text: '#1E293B'
              }
            : defaultTemplate.default_colors,
          logo_url: org.portal_settings?.logo,
          hero_image_url: org.portal_settings?.header_image,
          visible_sections: ['news', 'documents', 'contact', 'issues'],
          meta_title: org.name,
          meta_description: org.portal_settings?.description,
          footer_text: `© ${new Date().getFullYear()} ${org.name}`
        };
      }

      // 4. Get portal content data (news, documents, etc.)
      const contentData = await getPortalData(org.id, isAuthenticated);

      // 5. Build PortalData object
      const data: PortalData = {
        settings: portalSettings,
        organization: {
          id: org.id,
          name: org.name || '',
          slug: org.slug || slug
        },
        news: contentData.announcements.map(a => ({
          id: a.id || '',
          title: a.title || '',
          content: a.content || '',
          image_url: a.image_url,
          published_at: a.created_at || new Date().toISOString(),
          author: a.created_by_name
        })),
        documents: contentData.documents.map(d => ({
          id: d.id || '',
          name: d.name || '',
          category: d.category || 'Övrigt',
          file_url: d.file_url || '',
          file_size: d.file_size,
          uploaded_at: d.created_at || new Date().toISOString()
        })),
        calendar: contentData.calendarEvents.map(e => ({
          id: e.id || '',
          title: e.title || '',
          description: e.description,
          start_date: e.start_date || new Date().toISOString(),
          end_date: e.end_date,
          location: e.location
        })),
        gallery: contentData.galleryImages.map(img => ({
          id: img.id || '',
          url: img.url || '',
          thumbnail_url: img.thumbnail_url,
          caption: img.caption,
          uploaded_at: img.created_at || new Date().toISOString()
        })),
        contact: {
          board_email: portalSettings.hide_email ? undefined : org.contact_email,
          board_phone: portalSettings.hide_phone ? undefined : org.contact_phone,
          address: portalSettings.hide_address ? undefined : portalSettings.contact_address,
        }
      };

      // Cache the data
      setCachedData(PORTAL_CACHE_KEYS.FULL_DATA, slug, data);
      
      // Update state
      setPortalData(data);
      
      if (!isBackgroundFetch) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to load portal:', err);
      if (!isBackgroundFetch) {
        setError('Kunde inte ladda portalen. Försök igen senare.');
        setLoading(false);
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-500">Laddar portal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !portalData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md mx-4">
          <Buildings size={64} className="mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {error || 'Något gick fel'}
          </h1>
          <p className="text-slate-600 mb-6">
            Kontrollera att du har rätt adress eller kontakta din bostadsrättsförening.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  // Get the right template component
  const templateId = portalData.settings.template_id || 'modern';
  const TemplateComponent = TEMPLATE_COMPONENTS[templateId] || ModernTemplate;

  return <TemplateComponent data={portalData} />;
}
